import { ChevronLeft, ChevronRight, Download, Play, X } from 'lucide-react';
import { AnimatePresence, animate, motion, type Variants } from 'motion/react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '../../lib/cn.js';
import { EASE_OUT_QUINT } from '../motion.js';

/** Curva calma, de saída longa: a troca assenta devagar, sem tranco no começo. */
const SUAVE = [0.33, 1, 0.68, 1] as const;

/**
 * A troca de item: as duas fotos ficam na mesma caixa, uma sobre a outra — a
 * anterior se apaga enquanto a nova aparece, com um deslize curto, de 20px,
 * para o lado em que se andou. Nada sai do lugar no layout durante a troca; é
 * isso que tira o tremor. A primeira foto não desliza — ela cresce da miniatura.
 */
const DESLIZE: Variants = {
  entra: (direcao: number) => ({ opacity: 0, x: direcao * 20 }),
  centro: { opacity: 1, x: 0, transition: { duration: 0.5, ease: SUAVE } },
  sai: (direcao: number) => ({
    opacity: 0,
    x: direcao * -20,
    transition: { duration: 0.4, ease: SUAVE },
  }),
};

/**
 * A foto que abre cresce do lugar da miniatura até o centro — o olho segue a
 * foto em vez de procurá-la. É o FLIP: medida a posição final, a foto começa
 * onde a miniatura estava, na escala dela, e assenta em 450ms.
 */
function crescerDaMiniatura(imagem: HTMLElement, origem: DOMRect): void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const destino = imagem.getBoundingClientRect();
  if (destino.width === 0 || destino.height === 0) return;

  const escala = Math.max(origem.width / destino.width, origem.height / destino.height);
  const x = origem.left + origem.width / 2 - (destino.left + destino.width / 2);
  const y = origem.top + origem.height / 2 - (destino.top + destino.height / 2);

  void animate(
    imagem,
    { x: [x, 0], y: [y, 0], scale: [escala, 1], opacity: [1, 1] },
    { duration: 0.45, ease: EASE_OUT_QUINT },
  );
}

export interface LightboxItem {
  readonly id: string;
  /** Imagem: o arquivo. Vídeo: o player embutido do Bunny. */
  readonly url: string;
  readonly title: string;
  /** Linhas de metadado: data da foto, coordenada, autor. */
  readonly meta?: readonly string[] | undefined;
  readonly kind?: 'imagem' | 'video' | undefined;
  /** Capa do vídeo, usada na tira de miniaturas. */
  readonly thumbnailUrl?: string | undefined;
}

/**
 * Visualizador de imagem e vídeo.
 *
 * Abrir a foto numa aba nova entrega o arquivo cru, sem data, sem coordenada e
 * sem como passar para a próxima — e no celular, custa perder a tela. Aqui a
 * foto é vista no contexto, com os metadados que a tornam prova de fiscalização.
 *
 * Vídeo entra na mesma sequência, mas quem toca é o **player do Bunny**, num
 * `<iframe>`. Não é preguiça de fazer um player próprio: o vídeo é servido em
 * HLS com várias resoluções, e o `<video>` do navegador não lê HLS sozinho fora
 * do Safari. O player deles também resolve legenda, velocidade e tela cheia.
 *
 * Usa `<dialog>` nativo pelo mesmo motivo do modal: prisão de foco, `Esc` e
 * retorno do foco ao gatilho saem prontos do navegador.
 *
 * Em movimento (design.md §8, Visualizador de imagem): com `origin`, a foto
 * aberta cresce do lugar da miniatura até o centro; andar entre os itens faz o
 * novo entrar pelo lado para onde se andou. Sem `origin`, a foto só aparece.
 */
export function Lightbox({
  items,
  index,
  origin,
  onClose,
  onNavigate,
}: {
  items: readonly LightboxItem[];
  /** `null` mantém o visualizador fechado. */
  index: number | null;
  /** Onde estava a miniatura clicada: a primeira foto cresce dali até o centro. */
  origin?: DOMRect | null | undefined;
  onClose: () => void;
  onNavigate: (index: number) => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  /** Para que lado se andou, para a próxima foto entrar por ele. */
  const [direcao, setDirecao] = useState(1);
  /** A origem que ainda não foi usada: só a primeira foto, ao abrir, cresce dela. */
  const origemPendente = useRef<DOMRect | null>(null);
  /*
   * Qual foto terminou de carregar, e não um "carregou" solto.
   *
   * Guardando o booleano era preciso zerá-lo num efeito toda vez que o índice
   * mudava — e entre a troca de foto e o efeito o React desenhava um quadro com
   * a foto nova já opaca. Guardando o índice, a resposta sai da própria
   * renderização e não há intervalo nenhum.
   */
  const [loadedIndex, setLoadedIndex] = useState<number | null>(null);
  const loaded = loadedIndex === index;

  const open = index !== null;
  const current = index === null ? undefined : items[index];

  const go = useCallback(
    (step: number) => {
      if (index === null || items.length === 0) return;
      origemPendente.current = null;
      setDirecao(step > 0 ? 1 : -1);
      // Percorre em ciclo: chegar ao fim da vistoria volta ao começo.
      onNavigate((index + step + items.length) % items.length);
    },
    [index, items.length, onNavigate],
  );

  /*
   * Abre antes da pintura, e a origem fica guardada antes de qualquer foto
   * carregar: o `load` é assíncrono e sempre chega depois deste efeito — a
   * foto já em cache do navegador também.
   */
  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useLayoutEffect(() => {
    origemPendente.current = open ? (origin ?? null) : null;
  }, [open, origin]);

  /*
   * A anterior e a seguinte já vêm baixadas: andar para o lado troca na hora,
   * em vez de a foto nova aparecer no meio da troca, quando o arquivo chega.
   */
  useEffect(() => {
    if (index === null || items.length < 2) return;

    for (const passo of [1, -1]) {
      const vizinha = items[(index + passo + items.length) % items.length];
      if (vizinha && vizinha.kind !== 'video') new Image().src = vizinha.url;
    }
  }, [index, items]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleCancel = (event: Event) => {
      event.preventDefault();
      onClose();
    };

    const handleKey = (event: KeyboardEvent) => {
      /*
       * Com o player em foco, as setas são dele — pular para o próximo item no
       * meio de um vídeo, porque a pessoa quis avançar dez segundos, seria
       * exatamente o contrário do esperado.
       */
      if (document.activeElement?.tagName === 'IFRAME') return;
      if (event.key === 'ArrowRight') go(1);
      if (event.key === 'ArrowLeft') go(-1);
    };

    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('keydown', handleKey);
    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('keydown', handleKey);
    };
  }, [onClose, go]);

  return (
    <dialog
      ref={dialogRef}
      aria-label="Visualizador de imagem"
      className={cn(
        /*
         * Nunca rola. A foto que desliza na troca passa um fio da borda, e o
         * `<dialog>` aberto rola o que transborda: nascia uma barra de rolagem
         * no meio da troca, a área da foto encolhia 12px e voltava — era o
         * tremor. Cortado aqui, na borda da tela, e não na área da foto, para
         * a foto que cresce da miniatura não ser cortada no caminho.
         */
        'm-0 h-dvh max-h-none w-screen max-w-none overflow-hidden bg-transparent p-0 text-white',
        'backdrop:bg-scrim-strong',
      )}
      onClick={(event) => {
        // Clicar no fundo fecha; clicar na imagem ou nos controles, não.
        if (event.target === dialogRef.current) onClose();
      }}
    >
      {current ? (
        <div className="flex h-full flex-col" onClick={(event) => event.stopPropagation()}>
          <header className="flex shrink-0 items-start gap-4 p-4">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{current.title}</p>
              {current.meta && current.meta.length > 0 ? (
                <p className="mt-0.5 truncate font-mono text-[11px] text-white/55">
                  {current.meta.join(' · ')}
                </p>
              ) : null}
            </div>

            {items.length > 1 ? (
              <span className="tabular shrink-0 rounded-md bg-white/10 px-2 py-1 text-[12px] font-medium">
                {(index ?? 0) + 1} / {items.length}
              </span>
            ) : null}

            {current.kind === 'video' ? null : (
              <a
                href={current.url}
                download
                target="_blank"
                rel="noreferrer noopener"
                aria-label="Baixar imagem"
                title="Baixar imagem"
                className="grid size-9 shrink-0 place-items-center rounded-md text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              >
                <Download size={17} strokeWidth={1.5} aria-hidden />
              </a>
            )}

            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className="grid size-9 shrink-0 place-items-center rounded-md text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            >
              <X size={18} strokeWidth={1.5} aria-hidden />
            </button>
          </header>

          <div className="relative min-h-0 flex-1">
            <AnimatePresence initial={false} custom={direcao}>
              <motion.div
                key={current.id}
                custom={direcao}
                variants={DESLIZE}
                initial="entra"
                animate="centro"
                exit="sai"
                onClick={(event) => {
                  // A área vazia ao redor da imagem também fecha.
                  if (event.target === event.currentTarget) onClose();
                }}
                className="absolute inset-0 flex items-center justify-center px-2 pb-4"
              >
                {current.kind === 'video' ? (
                  <iframe
                    // A URL já traz token e prazo quando a biblioteca exige assinatura.
                    src={`${current.url}${current.url.includes('?') ? '&' : '?'}autoplay=false&preload=true`}
                    title={current.title}
                    onLoad={() => setLoadedIndex(index)}
                    allow="accelerometer; gyroscope; encrypted-media; picture-in-picture; fullscreen"
                    allowFullScreen
                    className={cn(
                      // 16:9 até onde couber; a moldura preta do player faz o resto.
                      'aspect-video max-h-full w-full max-w-[1200px] border-0 transition-opacity duration-300 ease-out',
                      loaded ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                ) : (
                  <img
                    src={current.url}
                    alt={current.title}
                    onLoad={(event) => {
                      setLoadedIndex(index);
                      const origem = origemPendente.current;
                      origemPendente.current = null;
                      if (origem) crescerDaMiniatura(event.currentTarget, origem);
                    }}
                    className={cn(
                      'max-h-full max-w-full object-contain transition-opacity duration-300 ease-out',
                      loaded ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                )}
              </motion.div>
            </AnimatePresence>

            {items.length > 1 ? (
              <>
                <button
                  type="button"
                  onClick={() => go(-1)}
                  aria-label="Imagem anterior"
                  className="absolute top-1/2 left-2 z-10 grid size-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                >
                  <ChevronLeft size={22} strokeWidth={1.5} aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => go(1)}
                  aria-label="Próxima imagem"
                  className="absolute top-1/2 right-2 z-10 grid size-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                >
                  <ChevronRight size={22} strokeWidth={1.5} aria-hidden />
                </button>
              </>
            ) : null}
          </div>

          {items.length > 1 ? (
            <nav
              aria-label="Miniaturas"
              className="flex shrink-0 gap-1.5 overflow-x-auto px-4 pb-4"
            >
              {items.map((item, position) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    origemPendente.current = null;
                    setDirecao(position > (index ?? 0) ? 1 : -1);
                    onNavigate(position);
                  }}
                  aria-label={`Ver imagem ${position + 1}`}
                  aria-current={position === index}
                  className={cn(
                    'size-14 shrink-0 overflow-hidden rounded-md transition-opacity',
                    position === index ? 'opacity-100' : 'opacity-40 hover:opacity-75',
                  )}
                >
                  <span className="relative block size-full bg-white/10">
                    {item.kind === 'video' && !item.thumbnailUrl ? null : (
                      <img
                        src={item.thumbnailUrl ?? item.url}
                        alt=""
                        loading="lazy"
                        className="size-full object-cover"
                      />
                    )}
                    {item.kind === 'video' ? (
                      <span className="absolute inset-0 grid place-items-center bg-black/25">
                        <Play size={15} strokeWidth={2} fill="currentColor" aria-hidden />
                      </span>
                    ) : null}
                  </span>
                </button>
              ))}
            </nav>
          ) : null}
        </div>
      ) : null}
    </dialog>
  );
}

/** Estado do visualizador, para não repetir `useState` em cada tela. */
export function useLightbox() {
  const [index, setIndex] = useState<number | null>(null);
  return {
    index,
    open: (position: number) => setIndex(position),
    close: () => setIndex(null),
    navigate: (position: number) => setIndex(position),
  };
}
